import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { createTrainingGroup } from "./api";
import DialogHeader from "./DialogHeader";

interface CreateGroupDialogProps {
  open: boolean;
  clubId: number | null;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
  onError: (message: string) => void;
}

const CreateGroupDialog: React.FC<CreateGroupDialogProps> = ({
  open,
  clubId,
  onClose,
  onSuccess,
  onError,
}) => {
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open) setName("");
  }, [open]);

  const handleCreate = async () => {
    if (clubId == null || !name.trim()) return;
    try {
      setCreating(true);
      await createTrainingGroup(clubId, name.trim());
      await onSuccess();
      onClose();
    } catch (err) {
      console.error("Error creating training group:", err);
      onError("Failed to create training group. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogHeader
        title="Create Training Group"
        onClose={onClose}
        closeDisabled={creating}
      />
      <DialogContent dividers>
        <TextField
          fullWidth
          label="Group name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Tuesday Shadows"
          variant="outlined"
          disabled={creating}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={creating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={creating || !name.trim()}
          startIcon={creating ? <CircularProgress size={20} /> : <Add />}
        >
          {creating ? "Creating…" : "Create Group"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateGroupDialog;
